import React, { useEffect, useState, createElement, isValidElement } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import { colors } from '../../themes'
import { fontSize } from '../../styles'

import styles, { wrapperSize, iconSize } from './styles'
/**
 * AccessibleIcon
 * @param {object} icon - accepts a zenicon react component.
 * @param {string} title - for accessibility, if this is not provided, aria-hidden will be set to true.
 * @param {string} size - font-size of the icon. Default is fontSize.medium.
 * @param {string} color - color of the icon. Default is colors.scorpion.
 * @param {string} className - css class name
 * @param {boolean} ariaHidden - title will be stripped if this is set to true. Default is false.
 * */
const AccessibleIcon = ({ icon, title, size = fontSize.medium, color = colors.scorpion, className, ariaHidden = true }) => {
  const [iconObject, setIconObject] = useState({ type: null, icon: null })

  const ariaHiddenValue = (!title && 'true') || ariaHidden.toString()

  useEffect(() => {
    if (!icon) return
    if (typeof icon === 'string') {
      // eslint-disable-next-line no-console
      console.warn('AccessibleIcon', `Passing in strings to the icon prop is deprecated. You passed in "${icon}"`)
      return
    }
    if (icon?.paths) {
      setIconObject({ type: 'object', icon })
      return
    }
    if (isValidElement(createElement(icon))) {
      setIconObject({ type: 'zenicon' })
      return
    }

    // Fallback warning if provided icon is not handled
    // eslint-disable-next-line no-console
    console.warn('AccessibleIcon', 'icon prop should be an string, object with paths or a React component')
  }, [icon])

  return (
    Boolean(icon && iconObject.type) && (
      <span className={cx(styles.wrapper, className, wrapperSize(size))}>
        {iconObject.type === 'zenicon' ? (
          <>
            {createElement(icon, {
              title,
              color,
              fontSize: size,
              'aria-hidden': ariaHiddenValue,
            })}
          </>
        ) : (
          <svg
            className={iconSize(size)}
            fill={color}
            width={1024}
            height={1024}
            viewBox="0 0 1024 1024"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-hidden={ariaHiddenValue}>
            {Boolean(title) && <title>{title}</title>}

            <path d={iconObject.icon.paths.join(' ')} />
          </svg>
        )}
      </span>
    )
  )
}

AccessibleIcon.propTypes = {
  title: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  className: PropTypes.string,
  ariaHidden: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.shape({})]).isRequired,
}
export default AccessibleIcon
